import Immutable from 'immutable';
import {asciifold} from 'utils';
import {getResults} from 'store/search/helpers';
import {findIndexByExactTime} from 'store/element-list/helpers';

// For stubbing in tests
exports.asciifold = asciifold;

export const parseSegments = (segments, speakers) => {
    if (!segments) return [];

    return segments.reduce((parsedSegments, segment, segmentIndex) => {
        const parsedSegment = {
            path: [segmentIndex],
            startTime: segment.start_time,
            endTime: segment.end_time,
            speakerChange: segment.speaker_change,
        };

        if (speakers && segment.speaker_id != null) parsedSegment.speaker = speakers[segment.speaker_id];

        if (!segment.sequences) return parsedSegments;

        const sequences = segment.sequences.reduce((parsedSequences, sequence, sequenceIndex) => {
            const parsedSequence = {
                path: [segmentIndex, sequenceIndex],
                startTime: sequence.start_time,
                endTime: sequence.end_time,
            };

            if (!sequence.tokens) return parsedSequences;

            const tokens = sequence.tokens.map((token, tokenIndex) => ({
                path: [segmentIndex, sequenceIndex, tokenIndex],
                startTime: token.start_time,
                endTime: token.end_time,
                displayAs: token.display_as,
                value: exports.asciifold(token.value),
                type: token.type,
            }));

            if (!tokens.length) return parsedSequences;

            parsedSequence.tokens = tokens;
            parsedSequences.push(parsedSequence);
            return parsedSequences;
        }, []);

        if (!sequences.length) return parsedSegments;

        parsedSegment.sequences = sequences;
        parsedSegments.push(parsedSegment);
        return parsedSegments;
    }, []);
};

export const parseTokens = (segments) => {
    if (!segments) return [];

    const tokens = [];
    segments.forEach(segment => {
        segment.sequences.forEach(sequence => {
            sequence.tokens.forEach(token => {
                if (token.type === 'word') tokens.push(token);
            });
        });
    });
    return tokens;
};

export const getSpeakersById = (speakers) => {
    if (!speakers) return {};

    const speakersById = {};
    speakers.forEach(speaker => {
        speakersById[speaker.id] = {
            id: speaker.id,
            displayAs: speaker.name,
        };
    });

    return speakersById;
};

export const getSpeakerTimeRangesById = (segments) => {
    if (!segments) return {};

    const timeRanges = {};

    let start = segments[0];
    let end = segments[0];

    segments.forEach(segment => {
        if (segment.speakerChange && start.speaker) {
            if (!timeRanges[start.speaker.id]) timeRanges[start.speaker.id] = [];
            timeRanges[start.speaker.id].push({
                startTime: start.startTime,
                endTime: end.endTime,
            });
            start = segment;
        }
        end = segment;
    });

    return timeRanges;
};

export const parseSpeakers = (speakers, segments) => {
    if (!speakers || !segments) return [];

    const whitelist = [];
    const timeRanges = getSpeakerTimeRangesById(segments);

    for (const segment of segments) {
        if (segment.speaker && timeRanges[segment.speaker.id] && !whitelist.includes(segment.speaker.id)) {
            let shouldWhitelist = false;
            for (const sequence of segment.sequences) {
                for (const token of sequence.tokens) {
                    if (token.type !== 'sound') {
                        shouldWhitelist = true;
                        break;
                    }
                }
                if (shouldWhitelist) break;
            }
            if (shouldWhitelist) whitelist.push(segment.speaker.id);
        }
    }

    return speakers
        .filter(speaker => whitelist.includes(speaker.id))
        .map(speaker => ({
            type: 'Speaker',
            id: speaker.id,
            displayAs: speaker.name,
            value: exports.asciifold(speaker.name),
            count: timeRanges[speaker.id].length,
            timeRanges: timeRanges[speaker.id],
        }));
};

export const parseTimeRanges = (timeRanges, videoDuration = 0, tolerance = 3000) => {
    if (!timeRanges) return [];

    const ranges = [];

    const sanitize = (value) => {
        if (0 <= value && value <= videoDuration) return value;
        if (-tolerance <= value && value < 0) return 0;
        if (videoDuration < value && value <= videoDuration + tolerance) return videoDuration;
        return null;
    };

    timeRanges.forEach(range => {
        const startTime = sanitize(range.start_time);
        const endTime = sanitize(range.end_time);
        if (startTime !== null && endTime !== null) ranges.push({startTime, endTime});
    });

    return ranges;
};

const parseEntities = (type, entities, videoDuration, filterEmpty = true) => {
    if (!entities) return [];

    let parsed = Object
        .keys(entities)
        .map(key => entities[key])
        .filter(({time_ranges, display_name}) => Boolean(time_ranges) && Boolean(display_name))
        .map(entity => {
            const timeRanges = parseTimeRanges(entity.time_ranges, videoDuration);
            return {
                type,
                displayAs: entity.display_name,
                value: exports.asciifold(entity.display_name),
                confidence: entity.confidence,
                count: timeRanges.length,
                timeRanges,
            };
        });

    if (filterEmpty) parsed = parsed.filter(entity => entity.count);

    return parsed;
};

export const parseTopics = (topics, videoDuration) => (
    parseEntities('Topic', topics, videoDuration)
);

export const parseKeywords = (keywords, videoDuration, partialElementList) => (
    parseEntities('Keyword', keywords, videoDuration, false)
        .map(keyword => {
            if (!keyword.count) {
                // Get missing timing data from the transcript (token search)
                const results = getResults(partialElementList, {
                    types: Immutable.List(['Transcript']),
                    query: keyword.value,
                }).toJS();
                keyword.timeRanges = results.map(result => ({
                    startTime: result.startTime,
                    endTime: result.endTime,
                    tokens: result.tokens,
                }));
                keyword.count = keyword.timeRanges.length;
            } else {
                // Prefetch the tokens for each occurrence of the keyword
                const allTokens = partialElementList.get('tokens');
                keyword.timeRanges.forEach(timeRange => {
                    const startIndex = findIndexByExactTime(allTokens, timeRange.startTime, 'startTime');
                    const endIndex = findIndexByExactTime(allTokens, timeRange.endTime, 'endTime');
                    timeRange.tokens = allTokens.slice(startIndex, endIndex + 1).toJS();
                    timeRange.tokens.forEach(token => {
                        token.range = [0, token.value.length];
                    });
                });
            }
            return keyword;
        })
        .filter(keyword => keyword.count)
);

export const parseElementList = (elementList, videoDuration, language = 'en') => {
    const segments = exports.parseSegments(elementList.segments, getSpeakersById(elementList.speakers));
    const tokens = exports.parseTokens(segments);

    const parsed = Immutable.fromJS({
        segments,
        tokens,
        language,
        entityTypes: [],
        entities: [],
    });

    let speakers = [];

    if (segments.filter(segment => segment.speaker && segment.speaker.id !== undefined)) {
        speakers = exports.parseSpeakers(elementList.speakers, segments);
    }

    const parsedEntities = [
        ['Keyword', 'Keywords', exports.parseKeywords(elementList.keywords, videoDuration, parsed)],
        ['Topic', 'Topics', exports.parseTopics(elementList.topics, videoDuration)],
        ['Speaker', 'Speakers', speakers],
    ];

    const entityTypes = [];
    const entities = [];
    parsedEntities.forEach(([displayAs, displayAsPlural, occurrences], index) => {
        if (occurrences.length) {
            entityTypes.push({
                index,
                displayAs,
                displayAsPlural,
                value: displayAs,
                count: occurrences.length,
            });
            entities.push(...occurrences);
        }
    });

    return parsed.merge({entityTypes, entities});
};



// WEBPACK FOOTER //
// ./scripts/store/element-list/parser.js